/**
 * Client-side champion ability POOL — AUTO-DERIVED from content (#323),
 * mirroring `catalog.ts`'s (#239) build-time `import.meta.glob` pattern: we
 * deliberately do NOT import the `@pit/content` runtime package into the
 * browser bundle (that would pull in `node:crypto` + the loader), so this
 * reads the RAW champion JSON's `abilities` id-list directly, the same
 * on-disk field the content loader resolves into `Champion.abilities`.
 *
 * This is the source of truth `LoadoutPicker` needs for "how many abilities
 * does this champion have, and what are their ids" — distinct from
 * `abilityLabels.ts`/`abilityFacts.ts` (auto-derived display/facts tables,
 * keyed by the SAME content-canonical slot index).
 */

import { z } from "zod";
import { ABILITY_ID_SCHEMA, CHAMPION_ID_SCHEMA, type AbilityId, type ChampionId } from "@pit/protocol";

const CHAMPION_POOL_SCHEMA = z.object({
  id: CHAMPION_ID_SCHEMA,
  abilities: z.array(ABILITY_ID_SCHEMA).min(1),
});

function loadContentPools(): ReadonlyMap<ChampionId, readonly AbilityId[]> {
  const modules = import.meta.glob<string>(
    "../../../../packages/content/data/champions/*.json",
    { eager: true, query: "?raw", import: "default" },
  );
  const out = new Map<ChampionId, readonly AbilityId[]>();
  for (const path of Object.keys(modules).sort()) {
    const raw = modules[path];
    if (raw === undefined) continue;
    const meta = CHAMPION_POOL_SCHEMA.parse(JSON.parse(raw) as unknown);
    out.set(meta.id, meta.abilities);
  }
  return out;
}

/** Every content champion's full ability pool, in content-canonical (slot)
 *  order, keyed by champion id. */
export const ABILITY_POOL_BY_CHAMPION: ReadonlyMap<ChampionId, readonly AbilityId[]> =
  loadContentPools();

/** A champion's full ability pool, or `[]` for an unknown id (defensive —
 *  every catalog champion has a content entry by construction). */
export function abilityPoolFor(championId: ChampionId): readonly AbilityId[] {
  return ABILITY_POOL_BY_CHAMPION.get(championId) ?? [];
}
